import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import toast from "react-hot-toast";
import { apiScopes } from "../auth/msalConfig";
import { useAppContext } from "../context/AppContext";
import { theme, tint } from "../theme";

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

// iOS Safari (and some mobile browsers) cannot incrementally read a streaming
// fetch body for a never-ending multipart/x-mixed-replace response — it either
// lacks response.body or buffers the whole stream — so the MJPEG /live parser
// yields no frames there.  On mobile we fall back to polling /snapshot instead.
// (navigator.platform === "MacIntel" + touch catches iPadOS, which reports as
// desktop Safari.)
const IS_MOBILE =
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod|Android/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

type StreamState = "idle" | "connecting" | "live" | "no-signal";

interface NewDetectionMessage {
  type: "new_detection";
  data: { detection_events: { type_name: string }[]; confidence_score: number };
}

// ── MJPEG frame parser ────────────────────────────────────────────────────────
// Reads a multipart/x-mixed-replace response body and extracts individual
// JPEG frames by scanning for SOI (0xFF 0xD8) and EOI (0xFF 0xD9) markers.
// JPEG byte-stuffing guarantees EOI only appears as the real end marker.

function findSeq(buf: Uint8Array, b0: number, b1: number, from = 0): number {
  for (let i = from; i < buf.length - 1; i++) {
    if (buf[i] === b0 && buf[i + 1] === b1) return i;
  }
  return -1;
}

function concat(a: Uint8Array<ArrayBuffer>, b: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> {
  const c = new Uint8Array(a.length + b.length);
  c.set(a);
  c.set(b, a.length);
  return c;
}

async function streamMjpeg(
  url: string,
  token: string,
  onFrame: (blob: Blob) => void,
  signal: AbortSignal,
): Promise<void> {
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

  const reader = resp.body.getReader();
  let buf = new Uint8Array(0);

  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done || signal.aborted) break;

    buf = concat(buf, value);

    // Extract all complete JPEG frames from the buffer
    while (true) {
      const soi = findSeq(buf, 0xff, 0xd8);
      if (soi === -1) { buf = new Uint8Array(0); break; }

      const eoi = findSeq(buf, 0xff, 0xd9, soi + 2);
      if (eoi === -1) { buf = buf.slice(soi); break; }   // partial — wait for more data

      onFrame(new Blob([buf.slice(soi, eoi + 2)], { type: "image/jpeg" }));
      buf = buf.slice(eoi + 2);
    }
  }
}

// ── Snapshot polling fallback (mobile) ────────────────────────────────────────
// Holds a /live request open purely to register as a viewer (so the backend
// tells the router to start pushing frames), and displays frames by polling the
// finite /snapshot endpoint — which every browser can fetch, unlike a streaming
// body.  Aborting the signal closes /live (-> stop_stream) and stops polling.

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function sleepAbortable(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => { clearTimeout(t); resolve(); }, { once: true });
  });
}

async function pollSnapshots(
  deviceId: string,
  token: string,
  onFrame: (blob: Blob) => void,
  signal: AbortSignal,
): Promise<void> {
  // No need to hold a /live request open: the backend treats each /snapshot
  // poll as a short-lived viewer heartbeat (TTL), so polling alone keeps the
  // router streaming and stops it when we stop polling.  (Holding an unread
  // /live open was unreliable — iOS closes it, dropping the viewer.)
  const POLL_MS = 500;
  let prev: Uint8Array | null = null;

  while (!signal.aborted) {
    try {
      // Cache-bust + no-store: iOS Safari otherwise serves the first snapshot
      // from its HTTP cache for every subsequent identical GET, so the frame
      // would never update.
      const r = await fetch(`${API_BASE}/api/stream/${deviceId}/snapshot?t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
        signal,
      });
      if (r.ok) {
        const bytes = new Uint8Array(await r.arrayBuffer());
        // Only repaint / re-timestamp on a genuinely new frame (the router
        // pushes ~1 fps, so most polls return the same cached JPEG).
        if (!prev || !bytesEqual(prev, bytes)) {
          prev = bytes;
          onFrame(new Blob([bytes], { type: "image/jpeg" }));
        }
      }
      // 404 = no frame yet (router not pushing); keep polling — the held /live
      // request above should make frames start flowing shortly.
    } catch {
      if (signal.aborted) return;
    }
    await sleepAbortable(POLL_MS, signal);
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CameraPage() {
  const { instance, accounts }     = useMsal();
  const { deviceId, lastMessage }  = useAppContext();
  const imgRef                     = useRef<HTMLImageElement>(null);
  const blobUrlRef                 = useRef<string | null>(null);
  const [streamState, setStreamState] = useState<StreamState>("idle");
  // Last received frame timestamp (epoch ms); null = no frame yet this session.
  const [lastFrameAt, setLastFrameAt] = useState<number | null>(null);
  // Tick once per second so the "Xs ago" display updates without re-rendering on every frame.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // ── Stream lifecycle ───────────────────────────────────────────────────────
  // Persistent reconnect loop: each iteration opens one /live request and
  // streams frames until the backend closes (30 s no-frame timeout or
  // router stopped) or the request errors.  The loop then waits a short
  // back-off and reconnects automatically, so the user never has to
  // refresh the page to recover from a temporary outage.
  useEffect(() => {
    if (!deviceId) {
      setStreamState("idle");
      return;
    }

    const controller = new AbortController();
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const RETRY_DELAY_MS = 3000;

    const runOnce = async () => {
      if (controller.signal.aborted) return;
      setStreamState("connecting");
      try {
        const { accessToken } = await instance.acquireTokenSilent({
          scopes:  apiScopes,
          account: accounts[0],
        });

        // Ask the backend when the latest frame actually arrived, so that
        // after a page reload we show the real age instead of "just now"
        // (the backend replays the cached frame to every new viewer).
        let serverLastFrameMs: number | null = null;
        try {
          const r = await fetch(
            `${API_BASE}/api/stream/${deviceId}/active`,
            { headers: { Authorization: `Bearer ${accessToken}` }, signal: controller.signal },
          );
          if (r.ok) {
            const j = await r.json() as { last_frame_at: string | null };
            if (j.last_frame_at) serverLastFrameMs = Date.parse(j.last_frame_at);
          }
        } catch { /* ignore — fall back to live timestamps */ }
        if (serverLastFrameMs !== null) setLastFrameAt(serverLastFrameMs);

        // Skip the very first frame's local timestamp — the backend yields
        // the cached frame on connect, which is older than Date.now().  The
        // /active call above seeded the correct age; subsequent frames are
        // genuinely new and update lastFrameAt to Date.now() as usual.
        let firstFrameSkipped = false;

        const onFrame = (blob: Blob) => {
          // Paint frame onto <img> via blob URL; revoke the previous one
          const url = URL.createObjectURL(blob);
          if (imgRef.current) imgRef.current.src = url;
          if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = url;
          setStreamState("live");

          if (!firstFrameSkipped) {
            firstFrameSkipped = true;
            // Trust the server-reported timestamp seeded above.  If the
            // server didn't have one yet (no_frame_at: null), fall back
            // to "now" so the badge shows something sensible.
            if (serverLastFrameMs === null) setLastFrameAt(Date.now());
            return;
          }
          setLastFrameAt(Date.now());
        };

        // Desktop: incremental MJPEG over a streaming fetch body.
        // Mobile (iOS Safari can't read streaming bodies): snapshot polling.
        if (IS_MOBILE) {
          await pollSnapshots(deviceId, accessToken, onFrame, controller.signal);
        } else {
          await streamMjpeg(
            `${API_BASE}/api/stream/${deviceId}/live`,
            accessToken,
            onFrame,
            controller.signal,
          );
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.warn("Stream error (will retry):", err);
        }
      }

      // Either the stream ended cleanly or errored.  Schedule a reconnect
      // unless the effect has been torn down (device change / unmount).
      if (controller.signal.aborted) return;
      setStreamState("no-signal");
      retryTimer = setTimeout(runOnce, RETRY_DELAY_MS);
    };

    runOnce();

    return () => {
      controller.abort();
      if (retryTimer) clearTimeout(retryTimer);
      // Clean up the last blob URL to avoid memory leaks
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      if (imgRef.current) imgRef.current.src = "";
      setStreamState("idle");
      setLastFrameAt(null);
    };
  }, [deviceId, instance, accounts]);

  // ── "Xs ago" formatter ─────────────────────────────────────────────────────
  const formatLastFrame = (): string | null => {
    if (lastFrameAt === null) return null;
    const sec = Math.max(0, Math.round((Date.now() - lastFrameAt) / 1000));
    if (sec < 1)  return "just now";
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    const rem = sec % 60;
    return rem === 0 ? `${min}m ago` : `${min}m ${rem}s ago`;
  };
  const lastFrameLabel = formatLastFrame();
  // Stale = no frame for more than 5 seconds while still nominally live
  const isStale = lastFrameAt !== null && Date.now() - lastFrameAt > 5000;
  // Very stale = no frame for more than 30 seconds — desaturate the image
  // so the operator sees at a glance that it is no longer current.
  const isVeryStale = lastFrameAt !== null && Date.now() - lastFrameAt > 30000;

  // ── Detection toast ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!lastMessage) return;
    const msg = lastMessage as Partial<NewDetectionMessage>;
    if (msg.type === "new_detection" && msg.data) {
      const label      = msg.data.detection_events?.map((e) => e.type_name).join(", ") ?? "Unknown";
      const confidence = Math.round((msg.data.confidence_score ?? 0) * 100);
      toast(`⚠ Anomaly: ${label} (${confidence}%)`, {
        style: {
          background: theme.bgCard,
          color:      theme.textPrimary,
          border:     `1px solid ${theme.gold}`,
        },
      });
    }
  }, [lastMessage]);

  // ── Status badge ───────────────────────────────────────────────────────────
  const stateLabel: Record<StreamState, { text: string; color: string }> = {
    idle:        { text: "No device selected", color: theme.textMuted },
    connecting:  { text: "Connecting…",        color: theme.warning   },
    live:        { text: "● Live",             color: theme.success   },
    "no-signal": { text: "No signal",          color: theme.danger    },
  };
  // Even while the /live request is technically still open, treat the
  // stream as stale once no frame has arrived for >30 s so the user does
  // not keep seeing "● Live" next to a frozen image.
  const badge = isVeryStale
    ? stateLabel["no-signal"]
    : stateLabel[streamState];

  // Once we have ever received a frame in this session, keep showing it
  // (greyed out when stale) instead of replacing it with the overlay.
  // Avoids the disorienting flicker when the backend cycles the /live
  // request every 30 s of inactivity.
  const hasFrame = lastFrameAt !== null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 28 }}>
      {/* No page title: the rail already names this view, and the status is the
          thing worth reading first here. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        {/* Status as a pill rather than loose text — it is the one piece of
            state on this page that changes on its own, so it gets a shape. */}
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "3px 10px", borderRadius: 11,
          border: `1px solid ${tint(badge.color, 33)}`,
          background: tint(badge.color, 10),
          color: badge.color,
          fontSize: theme.fontSize2Xs, fontWeight: 700, letterSpacing: "0.08em",
          textTransform: "uppercase", whiteSpace: "nowrap",
        }}>
          <span
            className={badge === stateLabel.live ? "dot-pulse" : undefined}
            style={{ width: 7, height: 7, borderRadius: "50%", background: badge.color, flexShrink: 0 }}
          />
          {badge.text.replace(/^●\s*/, "")}
        </span>
        {deviceId && (
          <span style={{ color: theme.textSecondary, fontSize: theme.fontSizeXs, fontFamily: theme.fontMono }}>
            {deviceId}
          </span>
        )}
        {lastFrameLabel && (
          <span
            title={`Last frame received at ${new Date(lastFrameAt!).toLocaleTimeString("en-GB")}`}
            style={{
              color:    isStale ? theme.warning : theme.textMuted,
              fontSize: 12,
              fontWeight: isStale ? 600 : 400,
              letterSpacing: "0.02em",
            }}
          >
            Last frame: {lastFrameLabel}
          </span>
        )}
      </div>

      <div style={{
        position:     "relative",
        width:        "100%",
        // Bounded by the viewport height, not by a fixed 720px: the frame is
        // square, so a height cap is what actually decides how big it can be,
        // and the old width cap left most of a tall window empty. min() keeps
        // it from overflowing a short one.
        maxWidth:     "min(100%, calc(100vh - 210px))",
        // Square, matching the centred crop the edge model runs on and the
        // detection cards display — so the live view frames the same region
        // the models actually see. Frontend-only crop (objectFit: cover), no
        // extra backend work.
        aspectRatio:  "1",
        background:   theme.bgCard,
        border:       `1px solid ${theme.border}`,
        borderRadius: 8,
        overflow:     "hidden",
      }}>
        {/* MJPEG frames are painted here by the stream loop */}
        <img
          ref={imgRef}
          alt="Live camera feed"
          style={{
            width:      "100%",
            height:     "100%",
            objectFit:  "cover",
            objectPosition: "center",
            display:    hasFrame ? "block" : "none",
            filter:     isVeryStale ? "grayscale(1)" : "none",
            transition: "filter 0.3s ease",
          }}
        />

        {/* Overlay only when we have nothing to show at all (idle / first
            connect / unrecoverable error before any frame ever arrived). */}
        {!hasFrame && streamState !== "live" && (
          <div style={{
            position:        "absolute",
            inset:           0,
            display:         "flex",
            flexDirection:   "column",
            alignItems:      "center",
            justifyContent:  "center",
            gap:             8,
            color:           theme.textMuted,
          }}>
            <span style={{ fontSize: 36 }}>
              {streamState === "connecting" ? "⏳" : "⊘"}
            </span>
            <span style={{ fontSize: 14 }}>{badge.text}</span>
            {streamState === "no-signal" && (
              <span style={{ fontSize: 12, color: theme.textMuted, opacity: 0.7 }}>
                Select a device and ensure the router is online
              </span>
            )}
          </div>
        )}

        {/* The square is not a styling choice, so it says so: it is the exact
            region the edge model evaluates. Without the chip the crop reads as
            an arbitrary frame and invites "why is the picture cut off". */}
        {hasFrame && (
          <span style={{
            position: "absolute", top: 10, left: 10,
            background: "rgba(26,28,30,0.72)", color: theme.onDark,
            fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
            padding: "3px 8px", borderRadius: 3, whiteSpace: "nowrap",
          }}>
            1:1 MODEL CROP
          </span>
        )}
      </div>

      {/* Info footer */}
      <p style={{
        marginTop: 12, fontSize: theme.fontSizeXs, color: theme.textMuted,
        display: "flex", alignItems: "flex-start", gap: 7, maxWidth: 720,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
             style={{ flexShrink: 0, marginTop: 2, opacity: 0.7 }}>
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/>
          <line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
        <span>
          The stream starts automatically when a device is selected and stops when you
          leave this page. The square crop matches the region the edge model evaluates.
        </span>
      </p>
    </div>
  );
}
